// Cell-builders for the per-view matrix path. Takes the
// per-entity wire shapes from `@lib/api-types/*` (returned by
// `useResilienceView` / `useTalentView` / `useRoadmapView`).
//
// Sister to `./matrix-cells.ts`, which holds the legacy-snapshot
// variants still used by views not yet migrated. Retires together
// with `data.ts` once every view consumes per-view payloads.

import type { TooltipContent } from './components/Tooltip.tsx';
import {
  RATING_LABELS,
  buildRatingIndex,
  buildSkillFunctions,
  coverageForCell,
  isInstitutional,
  levelClass,
} from './data-views.ts';
import type {
  BusinessFunction,
  DerivedContext,
  Engineer,
  RatingValue,
} from './data-views.ts';

export interface MatrixCellProps {
  value: number | null;
  levelClass: string;
  tooltip: TooltipContent | null;
}

/** Shared empty set — the non-Timeline case, where nothing is exiting. */
const EMPTY_EXITING: ReadonlySet<string> = new Set<string>();

/**
 * Coverage cell for a whole derivedContext in grouped mode. The number
 * shown is the *weakest-link* count — the minimum coverage across
 * the comp's skills for the given function — so a single under-
 * staffed skill drags the cell down. Skills that are EXITING at this
 * snapshot are excluded from that minimum; see `exitingSkillIds`.
 */
export function makeMergedCoverageCell(
  comp: DerivedContext,
  fn: BusinessFunction,
  threshold: RatingValue,
  levelLabel: string,
  ratingIndex: ReturnType<typeof buildRatingIndex>,
  skillFunctions: ReturnType<typeof buildSkillFunctions>,
  team: Engineer[],
  /**
   * Skills EXITING at this snapshot (Timeline's `diff.removedSkillIds`), which
   * must not drive the floor.
   *
   * `mergeSnapshotsForDiff` deliberately re-attaches removed skills to their
   * derivedContext so they can render struck-through. That makes them part of
   * `comp.skills`, and since a skill on its way out generally has nobody rated
   * on it, a plain `min` over the group collapsed the whole derivedContext to 0 —
   * reporting a coverage crisis on the one skill the org is deliberately
   * dropping. The floor should describe what the org still depends on.
   *
   * Omitted (or empty) off the Timeline, where nothing is exiting.
   */
  exitingSkillIds: ReadonlySet<string> = EMPTY_EXITING,
): MatrixCellProps {
  const coverage = coverageForCell(
    comp,
    fn,
    threshold,
    ratingIndex,
    skillFunctions,
    team,
  );
  if (coverage.length === 0) {
    return { value: null, levelClass: 'na', tooltip: null };
  }
  let minCount = Infinity;
  let anyInstitutional = false;
  const perSkill: Array<{
    name: string;
    count: number;
    institutional: boolean;
    exiting: boolean;
  }> = [];
  for (const { skill, covered } of coverage) {
    const inst = isInstitutional(skill);
    if (inst) anyInstitutional = true;
    const exiting = exitingSkillIds.has(skill.id);
    perSkill.push({
      name: skill.display,
      count: covered.length,
      institutional: inst,
      exiting,
    });
    // Exiting skills stay VISIBLE in the breakdown (the reader should still see
    // them) but are excluded from the floor.
    if (!exiting && covered.length < minCount) minCount = covered.length;
  }
  // `Infinity` here means every skill in the group is exiting. There is no
  // ongoing dependency left to measure, so fall back to the unfiltered floor
  // rather than inventing a 0 — the derivedContext is being retired wholesale, and
  // that is what the strikethrough already conveys.
  if (minCount === Infinity && perSkill.length > 0) {
    minCount = Math.min(...perSkill.map((p) => p.count));
  }
  const value = minCount === Infinity ? 0 : minCount;
  const tooltip: TooltipContent =
    value === 0
      ? {
          title: 'Coverage gap',
          body:
            'Skills with no ' +
            levelLabel +
            ' coverage:\n' +
            perSkill
              .filter((p) => p.count === 0 && !p.exiting)
              .map((p) => p.name)
              .join('\n'),
          sub: anyInstitutional
            ? 'Group includes institutional-knowledge skills'
            : undefined,
          kind: 'alert',
        }
      : {
          title: `${value} engineer${value === 1 ? '' : 's'} on the weakest skill`,
          body:
            'Per-skill ' +
            levelLabel +
            ' coverage:\n' +
            perSkill
              .map((p) => `${p.name} — ${p.count}${p.exiting ? ' (exiting)' : ''}`)
              .join('\n'),
          sub: anyInstitutional
            ? 'Group includes institutional-knowledge skills'
            : undefined,
          kind: value === 1 ? 'alert' : 'neutral',
        };
  return { value, levelClass: levelClass(value), tooltip };
}

/**
 * Required-talent cell for a whole derivedContext in grouped mode
 * (Roadmap). The level shown is the *max* required across the
 * comp's skills for the given function — strictest-bar semantic.
 * Returns an `na` cell if no skill in the comp both applies to
 * the function and has a requirement.
 */
export function makeMergedRequirementCell(
  comp: DerivedContext,
  fn: BusinessFunction,
  requirementBySkill: Map<string, { level: RatingValue; roles: Set<string> }>,
  skillFunctions: Map<string, Set<string>>,
): MatrixCellProps {
  let maxLevel = 0;
  const perSkill: Array<{ name: string; level: RatingValue }> = [];
  for (const skill of comp.skills) {
    const req = requirementBySkill.get(skill.id);
    if (!req) continue;
    const fns = skillFunctions.get(skill.id);
    if (!fns || !fns.has(fn.id)) continue;
    perSkill.push({ name: skill.display, level: req.level });
    if (req.level > maxLevel) maxLevel = req.level;
  }
  if (perSkill.length === 0) {
    return { value: null, levelClass: 'na', tooltip: null };
  }
  const tooltip: TooltipContent = {
    title: `Required: ${RATING_LABELS[maxLevel as RatingValue]} (strictest)`,
    body: perSkill
      .map((p) => `${p.name} — ${RATING_LABELS[p.level]}`)
      .join('\n'),
  };
  return {
    value: maxLevel,
    levelClass: `teal lvl-${maxLevel}`,
    tooltip,
  };
}
